import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import axios from 'axios';

interface ScanFormData {
  csp_code: string;
  csp_name: string | null;
  user_code: string;
  user_mobile: string;
}

interface CspDetails {
  csp_code: string;
  csp_name: string | null;
}

export default function ScanForm() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ScanFormData>({
    csp_code: '',
    csp_name: null,
    user_code: '',
    user_mobile: ''
  });

  // Fetch CSP details on load
  useEffect(() => {
    const cspCode = searchParams.get('csp');
    if (!cspCode) {
      setError('No CSP code provided');
      setLoading(false);
      return;
    }

    axios.get<CspDetails>(`https://csp-tracking-api.drishtee.in/api/csp/${cspCode}`)
      .then(response => {
        setFormData(prev => ({
          ...prev,
          csp_code: response.data.csp_code,
          csp_name: response.data.csp_name
        }));
      })
      .catch(err => {
        setError('Failed to fetch CSP details');
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Get geolocation
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000
        });
      });

      const { latitude: lat, longitude: lng, accuracy  } = position.coords;

      // Submit registration
      await axios.post('https://csp-tracking-api.drishtee.in/api/user-registrations', {
        ...formData,
        lat,
        lng,
        accuracy
      });

      // Show success message
      alert('successful!');
      
      // Clear form
      setFormData(prev => ({
        ...prev,
        user_code: '',
        user_mobile: ''
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          User Registration
        </Typography>

        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="CSP Name"
              value={formData.csp_name || ''}
              disabled
              margin="normal"
            />
            <TextField
              fullWidth
              label="CSP Code"
              value={formData.csp_code}
              disabled
              margin="normal"
            />
            <TextField
              fullWidth
              label="User Code"
              name="user_code"
              value={formData.user_code}
              onChange={handleChange}
              required
              margin="normal"
            />
            <TextField
              fullWidth
              label="Mobile"
              name="user_mobile"
              value={formData.user_mobile}
              onChange={handleChange}
              required
              margin="normal"
              placeholder="+919876543210"
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Register'}
            </Button>
            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
          </form>
        </Paper>
      </Box>
    </Container>
  );
}
